/**
 * Bottleneck Detection service.
 *
 * Reviews completed production task records and surfaces repeated slowdowns or
 * performance issues by grouping (task, room, product, time of day, worker) plus
 * recurring issue notes. Admin-only — uses labour cost fields, so it must never
 * run in a production-user context.
 *
 * Design principles (mirrors the analytics logic already in the codebase):
 *  - Completed, non-archived tasks only. The caller (useAdminRecentTasks) already
 *    scopes by companyId + status + archived + date range, so detection respects
 *    the active dashboard date filter automatically.
 *  - Median-based comparison to reduce outlier distortion.
 *  - Task-mix adjusted: room / worker / time-of-day groups are compared as a ratio
 *    against the typical time for the SAME task category, never raw duration —
 *    so a room that only runs naturally long tasks is not unfairly flagged.
 *  - Patterns, not one-off runs: a group must clear a minimum sample size before
 *    it can be a confident bottleneck. Below that it is still surfaced, but tagged
 *    as limited-data and framed as an early signal.
 *  - There is no expected/target-time field in the model, so comparisons use
 *    historical baselines (peer median for the same task, or the group's own
 *    earlier runs for the trend check).
 *  - Worker-level insights are framed around process, never blame, and are capped
 *    at the 'amber' severity so a person is never flagged as a 'red' problem.
 */

import i18n from '../i18n';
import { formatCurrency, formatDuration } from './labour';
import { getBenchmarkKey } from './benchmark';

// ── Tunables ──────────────────────────────────────────────────────
const MIN_DURATION_MINUTES = 3;        // below this = likely test / bad data
const MAX_DURATION_MINUTES = 16 * 60;  // above this = implausible / forgotten timer
const MIN_SAMPLE = 3;                  // confident pattern threshold per group
const RED_SAMPLE = 6;                  // sustained pattern threshold
const MIN_CATEGORY_RUNS = 2;           // a task category needs this many runs to be a baseline
const MIN_GROUPS = 2;                  // need at least 2 groups to compare a dimension

const AMBER_DELTA = 15;  // % over baseline → repeated issue
const RED_DELTA = 40;    // % over baseline → severe / persistent

const TREND_MIN_RUNS = 5;     // minimum runs before a "slower than before" trend is trustworthy
const COST_AMBER_DELTA = 15;  // % over median cost/unit
const COST_RED_DELTA = 40;

const REWORK_AMBER_COUNT = 3; // issue-note runs needed for an amber signal
const REWORK_RED_COUNT = 6;

// Note categories that represent a recoverable production issue (from
// NOTE_CATEGORIES in useTaskNotes). Each maps to a short action hint key.
const ISSUE_NOTE_CATEGORIES = {
  'rework-required': { labelKey: 'noteCategories.rework-required', actionKey: 'bottlenecks.repeatedIssue.action.rework-required' },
  'material-delay': { labelKey: 'noteCategories.material-delay', actionKey: 'bottlenecks.repeatedIssue.action.material-delay' },
  'equipment-issue': { labelKey: 'noteCategories.equipment-issue', actionKey: 'bottlenecks.repeatedIssue.action.equipment-issue' },
  'quality-concern': { labelKey: 'noteCategories.quality-concern', actionKey: 'bottlenecks.repeatedIssue.action.quality-concern' },
  'setup-longer': { labelKey: 'noteCategories.setup-longer', actionKey: 'bottlenecks.repeatedIssue.action.setup-longer' },
  'staffing-issue': { labelKey: 'noteCategories.staffing-issue', actionKey: 'bottlenecks.repeatedIssue.action.staffing-issue' },
};

// ── Small math helpers ────────────────────────────────────────────
function median(nums) {
  if (!nums.length) return null;
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

// ── Validity + field accessors ────────────────────────────────────
function isValidTask(t) {
  if (!t || !t.companyId) return false;
  if (!t.startTime || !t.endTime) return false;
  const d = t.durationMinutes;
  if (d == null || d < MIN_DURATION_MINUTES || d > MAX_DURATION_MINUTES) return false;
  return true;
}

// Net working time (downtime removed) is the fair basis for duration comparison.
function netMinutes(t) {
  return t.netDurationMinutes ?? t.durationMinutes ?? null;
}

function submittedMs(t) {
  const d = t.submittedAt?.toDate?.() ?? (t.submittedAt ? new Date(t.submittedAt) : null);
  return d ? d.getTime() : 0;
}

function taskLabel(t) {
  return (
    t.taskCategoryName ||
    (t.isCustomTaskName ? t.customTaskName : t.taskName) ||
    i18n.t('bottlenecks.unnamedTask')
  );
}

// Time-of-day "shift" derived from start time — the model has no explicit shift
// field, but start time is always present, so this is a reliable proxy.
function shiftBucket(t) {
  const d = t.startTime?.toDate?.() ?? (t.startTime ? new Date(t.startTime) : null);
  if (!d || isNaN(d)) return null;
  const h = d.getHours();
  if (h >= 5 && h < 12) return { key: 'morning', label: i18n.t('bottlenecks.shifts.morning') };
  if (h >= 12 && h < 17) return { key: 'afternoon', label: i18n.t('bottlenecks.shifts.afternoon') };
  if (h >= 17 && h < 22) return { key: 'evening', label: i18n.t('bottlenecks.shifts.evening') };
  return { key: 'night', label: i18n.t('bottlenecks.shifts.night') };
}

// ── Severity ──────────────────────────────────────────────────────
// neutral 'info' for early/limited signals, 'amber' for repeated issues,
// 'red' only for severe + sustained ones.
function severityFor(deltaPct, sample, { capAmber = false } = {}) {
  if (sample < MIN_SAMPLE) return 'info';
  if (!capAmber && deltaPct >= RED_DELTA && sample >= RED_SAMPLE) return 'red';
  if (deltaPct >= AMBER_DELTA) return 'amber';
  return 'info';
}

const SEV_RANK = { red: 3, amber: 2, info: 1 };

function limitedNote(sample) {
  return sample < MIN_SAMPLE
    ? i18n.t('bottlenecks.limitedDataNote')
    : '';
}

// ── Per-task-category baselines (median net minutes) ──────────────
// Used to task-mix adjust the cross-group dimensions.
function categoryBaselines(workTasks) {
  const groups = new Map();
  for (const t of workTasks) {
    const key = getBenchmarkKey(t);
    if (!groups.has(key)) groups.set(key, []);
    const n = netMinutes(t);
    if (n != null) groups.get(key).push(n);
  }
  const baselines = new Map();
  for (const [key, arr] of groups) {
    if (arr.length >= MIN_CATEGORY_RUNS) baselines.set(key, median(arr));
  }
  return baselines;
}

/**
 * Cross-group, task-mix-adjusted slowdown detection for one dimension
 * (room / worker / time-of-day). Each task contributes a ratio of its net time
 * to the typical time for the SAME task category; the group's median ratio shows
 * whether it runs comparable work slower than the rest of the floor.
 *
 * @returns {object|null} the single worst group as a bottleneck candidate, or null.
 */
function detectCrossSlowdown(workTasks, baselines, dim) {
  // Build groups: { key → { name, ratios:[], minutes:[], excess } }
  const groups = new Map();
  let groupCount = 0;

  for (const t of workTasks) {
    const baseline = baselines.get(getBenchmarkKey(t));
    const n = netMinutes(t);
    if (!baseline || n == null) continue;

    // A worker dimension fans one task out to every assigned worker; all other
    // dimensions yield a single group key per task.
    const entries = dim.expand(t);
    for (const { key, name } of entries) {
      if (!key) continue;
      if (!groups.has(key)) { groups.set(key, { name, ratios: [], excess: 0 }); }
      const g = groups.get(key);
      g.ratios.push(n / baseline);
      g.excess += Math.max(0, n - baseline);
    }
  }

  groupCount = groups.size;
  if (groupCount < MIN_GROUPS) return null;

  let worst = null;
  for (const [key, g] of groups) {
    if (g.ratios.length < MIN_SAMPLE) continue; // ignore thin groups for the "worst" pick
    const medianRatio = median(g.ratios);
    const deltaPct = (medianRatio - 1) * 100;
    if (deltaPct < AMBER_DELTA) continue;
    if (!worst || deltaPct > worst.deltaPct) {
      worst = { key, name: g.name, sample: g.ratios.length, deltaPct, excess: g.excess };
    }
  }

  if (!worst) return null;
  return worst;
}

// ── Dimension definitions ─────────────────────────────────────────
const CROSS_DIMENSIONS = [
  {
    id: 'room',
    expand: (t) => [{ key: t.roomId || (t.roomName ? `name:${t.roomName}` : null), name: t.roomName || i18n.t('bottlenecks.unassignedRoom') }],
    build: (w) => ({
      title: i18n.t('bottlenecks.roomSlowdown.title', { name: w.name }),
      body: i18n.t('bottlenecks.roomSlowdown.body', { name: w.name, pct: Math.round(w.deltaPct) }),
      action: i18n.t('bottlenecks.roomSlowdown.action'),
    }),
  },
  {
    id: 'shift',
    expand: (t) => { const b = shiftBucket(t); return [{ key: b?.key, name: b?.label }]; },
    build: (w) => ({
      title: i18n.t('bottlenecks.shiftSlowdown.title', { name: w.name }),
      body: i18n.t('bottlenecks.shiftSlowdown.body', {
        pct: Math.round(w.deltaPct),
        shift: w.name.toLowerCase(),
      }),
      action: i18n.t('bottlenecks.shiftSlowdown.action'),
    }),
  },
  {
    id: 'worker',
    capAmber: true, // never escalate a person to 'red'
    expand: (t) => (t.employeeNames || []).map((name) => ({ key: `w:${name}`, name })),
    build: (w) => ({
      title: i18n.t('bottlenecks.workerSlowdown.title', { name: w.name }),
      body: i18n.t('bottlenecks.workerSlowdown.body', { name: w.name, pct: Math.round(w.deltaPct) }),
      action: i18n.t('bottlenecks.workerSlowdown.action'),
    }),
  },
];

// ── Product cost-per-unit bottleneck ──────────────────────────────
function detectProductCost(workTasks, currency) {
  const costed = workTasks.filter((t) => t.costPerUnit != null && t.costPerUnit > 0 && t.productName);
  const byProduct = new Map();
  for (const t of costed) {
    const key = t.productName;
    if (!byProduct.has(key)) byProduct.set(key, []);
    byProduct.get(key).push(t.costPerUnit);
  }
  if (byProduct.size < MIN_GROUPS) return null;

  const overall = median(costed.map((t) => t.costPerUnit));
  if (!overall) return null;

  let worst = null;
  for (const [name, arr] of byProduct) {
    const m = median(arr);
    const deltaPct = ((m - overall) / overall) * 100;
    if (deltaPct < COST_AMBER_DELTA) continue;
    if (!worst || deltaPct > worst.deltaPct) {
      worst = { name, sample: arr.length, deltaPct, costPerUnit: m };
    }
  }
  if (!worst) return null;

  const sev = worst.sample < MIN_SAMPLE
    ? 'info'
    : (worst.deltaPct >= COST_RED_DELTA && worst.sample >= RED_SAMPLE ? 'red' : 'amber');

  return {
    id: `product-cost:${worst.name}`,
    dimension: 'product',
    severity: sev,
    deltaPct: worst.deltaPct,
    sample: worst.sample,
    title: i18n.t('bottlenecks.productCost.title', { name: worst.name }),
    metric: formatCurrency(worst.costPerUnit, currency),
    body: `${i18n.t('bottlenecks.productCost.body', { name: worst.name, pct: Math.round(worst.deltaPct) })}${limitedNote(worst.sample)}`,
    action: i18n.t('bottlenecks.productCost.action'),
  };
}

// ── Task-category "slower than before" trend ──────────────────────
function detectTaskTrends(workTasks) {
  const groups = new Map();
  for (const t of workTasks) {
    const key = getBenchmarkKey(t);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(t);
  }

  let worst = null;
  for (const [, arr] of groups) {
    if (arr.length < TREND_MIN_RUNS) continue;
    const sorted = [...arr].sort((a, b) => submittedMs(a) - submittedMs(b));
    const half = Math.floor(sorted.length / 2);
    const earlier = sorted.slice(0, half).map(netMinutes).filter((n) => n != null);
    const recent = sorted.slice(half).map(netMinutes).filter((n) => n != null);
    if (earlier.length < 2 || recent.length < 2) continue;

    const em = median(earlier);
    const rm = median(recent);
    if (!em) continue;
    const deltaPct = ((rm - em) / em) * 100;
    if (deltaPct < AMBER_DELTA) continue;
    if (!worst || deltaPct > worst.deltaPct) {
      worst = { name: taskLabel(sorted[0]), sample: sorted.length, deltaPct, earlier: em, recent: rm };
    }
  }
  if (!worst) return null;

  return {
    id: `task-trend:${worst.name}`,
    dimension: 'task',
    severity: severityFor(worst.deltaPct, worst.sample),
    deltaPct: worst.deltaPct,
    sample: worst.sample,
    title: i18n.t('bottlenecks.taskTrend.title', { name: worst.name }),
    metric: `+${Math.round(worst.deltaPct)}%`,
    body: i18n.t('bottlenecks.taskTrend.body', {
      pct: Math.round(worst.deltaPct),
      recent: formatDuration(Math.round(worst.recent)),
      earlier: formatDuration(Math.round(worst.earlier)),
      count: worst.sample,
    }),
    action: i18n.t('bottlenecks.taskTrend.action'),
  };
}

// ── Recurring issue notes (rework / material / equipment) ─────────
function detectIssueNotes(workTasks, notes) {
  if (!notes?.length) return null;

  // Only count notes attached to a valid completed task in the current pool.
  const taskById = new Map(workTasks.map((t) => [t.id, t]));
  const byCategory = new Map(); // noteCategory → Set<taskId>
  const catContext = new Map(); // noteCategory → { taskName → count }

  for (const note of notes) {
    if (note.archived) continue;
    const cfg = ISSUE_NOTE_CATEGORIES[note.category];
    if (!cfg) continue;
    const task = taskById.get(note.taskId);
    if (!task) continue;
    if (!byCategory.has(note.category)) { byCategory.set(note.category, new Set()); catContext.set(note.category, {}); }
    byCategory.get(note.category).add(note.taskId);
    const label = taskLabel(task);
    const ctx = catContext.get(note.category);
    ctx[label] = (ctx[label] || 0) + 1;
  }

  let worst = null;
  for (const [category, taskSet] of byCategory) {
    const count = taskSet.size;
    if (count < REWORK_AMBER_COUNT) continue;
    if (!worst || count > worst.count) worst = { category, count };
  }
  if (!worst) return null;

  const cfg = ISSUE_NOTE_CATEGORIES[worst.category];
  const label = i18n.t(cfg.labelKey);
  const ctx = catContext.get(worst.category);
  const topTask = Object.entries(ctx).sort((a, b) => b[1] - a[1])[0];
  const context = topTask && topTask[1] >= 2
    ? i18n.t('bottlenecks.mostOftenOn', { name: topTask[0] })
    : '';
  const sev = worst.count >= REWORK_RED_COUNT ? 'red' : 'amber';

  return {
    id: `notes:${worst.category}`,
    dimension: 'rework',
    severity: sev,
    deltaPct: 0,
    sample: worst.count,
    title: i18n.t('bottlenecks.repeatedIssue.title', { label }),
    metric: i18n.t('bottlenecks.runsMetric', { count: worst.count }),
    body: `${i18n.t('bottlenecks.repeatedIssue.body', { label, count: worst.count })}${context}`,
    action: i18n.t(cfg.actionKey),
  };
}

/**
 * Main entry point.
 *
 * @param {object[]} tasks - completed, non-archived tasks (already account-scoped + date-filtered)
 * @param {object} [options]
 * @param {object[]} [options.notes] - company taskNotes for joining recurring-issue signals
 * @param {string}  [options.currency] - ISO currency for cost copy (default CAD)
 * @param {number}  [options.maxResults] - cap on returned cards (default 3)
 * @returns {{ bottlenecks: object[], analysedCount: number, hasEnoughData: boolean }}
 */
export function detectBottlenecks(tasks, options = {}) {
  const { notes = [], currency = 'CAD', maxResults = 3 } = options;

  const valid = (tasks || []).filter(isValidTask);
  // Delay-type tasks are explicit downtime logs, not production work, so they are
  // excluded from duration/cost groupings to avoid skewing comparisons.
  const workTasks = valid.filter((t) => t.taskType !== 'delay');

  const result = { bottlenecks: [], analysedCount: workTasks.length, hasEnoughData: workTasks.length >= MIN_SAMPLE };
  if (!result.hasEnoughData) return result;

  const baselines = categoryBaselines(workTasks);
  const candidates = [];

  // Cross-group slowdowns (room / shift / worker), task-mix adjusted.
  for (const dim of CROSS_DIMENSIONS) {
    const w = detectCrossSlowdown(workTasks, baselines, dim);
    if (!w) continue;
    const copy = dim.build(w);
    candidates.push({
      id: `${dim.id}:${w.key}`,
      dimension: dim.id,
      severity: severityFor(w.deltaPct, w.sample, { capAmber: dim.capAmber }),
      deltaPct: w.deltaPct,
      sample: w.sample,
      title: copy.title,
      metric: `+${Math.round(w.deltaPct)}%`,
      body: `${copy.body}${i18n.t('bottlenecks.basedOnRuns', { count: w.sample })}${limitedNote(w.sample)}`,
      action: copy.action,
    });
  }

  // Task-category trend, product cost, recurring issue notes.
  const trend = detectTaskTrends(workTasks);
  if (trend) candidates.push(trend);

  const productCost = detectProductCost(workTasks, currency);
  if (productCost) candidates.push(productCost);

  const issue = detectIssueNotes(workTasks, notes);
  if (issue) candidates.push(issue);

  // Prioritise: severity tier, then magnitude, then sample size. Cap the list so
  // admins see the top signals, not a noisy wall.
  candidates.sort((a, b) => {
    const sr = SEV_RANK[b.severity] - SEV_RANK[a.severity];
    if (sr) return sr;
    if (b.deltaPct !== a.deltaPct) return b.deltaPct - a.deltaPct;
    return b.sample - a.sample;
  });

  result.bottlenecks = candidates.slice(0, maxResults);
  return result;
}
